/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { JobSectionsDaoService } from '../job-section-dao.service';
import {
  DbPersistenceService,
  Query
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { JobSection } from 'src/app/_model/job-section';
import { IMSId } from 'src/app/_model/ims-object';
import { JobSectionDbTableDefinition } from './job-section-db-table-definition';

@Injectable()
export class JobSectionsDbDaoService implements JobSectionsDaoService {

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private jobSectionTableDefinition: JobSectionDbTableDefinition,
  ) { }

  public async saveJobSections(jobSections: JobSection[]): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      var jobSectionsQueries: Query[] = [];
      for (const jobSection of jobSections) {
        let jobSectionQuery: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
             this.jobSectionTableDefinition.tableName +
            '(id, label, databag, sjobid, sjoblabel)' + // 3
            ' VALUES (?,?,?,?,?)',
          parameters: [
            Number(jobSection.isLocal() ? jobSection.id.secondaryId : jobSection.id.imsId),
            jobSection.label,
            jobSection.databag ? JSON.stringify(jobSection.databag) : undefined,
            jobSection.servicejob
              ? Number(jobSection.servicejob.getImsId())
              : undefined,
            jobSection.servicejob ? jobSection.servicejob.label : undefined,
          ]
        };

        jobSectionsQueries.push(jobSectionQuery);
      }
      await this.dbPersistenceService.executeBatch(db, jobSectionsQueries);
    } catch (error) {
      this.logger.log(
        'Error saving jobsections to db',
        error,
        '-',
        error.message
      );
    }
  }

  public async getJobSections(): Promise<JobSection[]> {
    let db = await this.dbPersistenceService.getImsDb();
    let jobSections: JobSection[] = [];

    try {
      // Get Synced JobSections
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.jobSectionTableDefinition.tableName + ';',
        parameters: null
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        jobSections.push(
          JobSection.fromDto({
            id: row['id'],
            secondaryid: undefined,
            label: row['label'],
            servicejob: {
              id: row['sjobid'],
              label: row['sjoblabel']
            }
          }, row['databag'] ? JSON.parse(row['databag']): undefined)
        );
      }
    } catch (error) {
      this.logger.log('Error reading jobsections from db', error);
    }
    
    return jobSections;
  }

  public async getJobSectionsForServiceJob(id: IMSId): Promise<JobSection[]> {
    if (id === undefined) {
      return []
    }

    let db = await this.dbPersistenceService.getImsDb();
    let jobSections: JobSection[] = [];

    try {
      // Get Synced JobSections
      let query = {
        statement: 'SELECT * FROM ' + this.jobSectionTableDefinition.tableName + ' WHERE sjobid = (?);',
        parameters: [id.imsId]
      }
      let data = await this.dbPersistenceService.executeQuery(db, query);
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        jobSections.push(
          JobSection.fromDto({
            id: row['id'],
            secondaryid: undefined,
            label: row['label'],
            servicejob: {
              id: row['sjobid'],
              label: row['sjoblabel']
            }
          }, row['databag'] ? JSON.parse(row['databag']): undefined)
        );
      }
    } catch (error) {
      this.logger.log('Error reading jobsections from db', error);
    }
    
    return jobSections;
  }

  public async reset() {}

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(this.constructor.name, "truncate table", this.jobSectionTableDefinition.tableName)
    await this.dbPersistenceService.truncateTable(db, this.jobSectionTableDefinition.tableName);
  }
}
